import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import type { SingleValue } from 'react-select';

import { Select } from '@/_components';
import type { AdvanceFilter } from '@/_containers';
import { FilterApplyTrigger, FilterTypeSelector, getFilterKey, getFilterValue, useFiltersState } from '@/_containers';
import { snackbar } from '@/_shared';

export function SelectFilterWithRadios<T>({
  handleSubmitAdvFilters,
  options,
  getOptionLabel,
  ...perFilter
}: AdvanceFilter<T> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
  options: { name: string; value: string }[];
  getOptionLabel: (option: { name: string; value: string }) => string;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, filterValue, applied } = perFilter;
  const isValueBasedFilter = mainKey === 'annual_meeting_month';
  const [refOne, setRefOne] = useState<HTMLInputElement | null>(null);
  const [refTwo, setRefTwo] = useState<HTMLInputElement | null>(null);

  const {
    fromState: [fromValue, setFromValue],
    toState: [toValue, setToValue],
    typeState: [type, setType],
    showTwoValues,
    inputValueRequired,
    transition: [isPending, startTransition],
  } = useFiltersState(
    null as SingleValue<{
      name: string;
      value: string;
    }> | null,
    types,
    selectedType
  );

  useLayoutEffect(() => {
    if (!filterValue || !applied) {
      return;
    }
    const values = filterValue.split('|||');
    if (values.length > 0) {
      const v = options.find((f) => f.name === values[0] || f.value === values[0]);
      if (v && fromValue === null) {
        setFromValue(v);
      }
      if (values.length > 1) {
        const v = options.find((f) => f.name === values[1] || f.value === values[1]);
        if (v && toValue === null) {
          setToValue(v);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, filterValue]);

  useEffect(() => {
    if (!applied) {
      setFromValue(null);
      setToValue(null);
      setType(null);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore clear contact value
      refOne?.clearValue();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore clear contact value
      refTwo?.clearValue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const handleSubmit = useCallback(() => {
    if (fromValue === null && inputValueRequired) {
      snackbar.error('Please select a value');
      return;
    }

    if (toValue === null && showTwoValues && inputValueRequired) {
      snackbar.error('Please select the second value');
      return;
    }

    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };

    const resolveSelectApiValue = (option: { name: string; value: string } | null) => {
      if (!option) {
        return '';
      }
      if (subKey && Object.prototype.hasOwnProperty.call(option, subKey)) {
        return String(option[subKey as keyof typeof option]);
      }
      return option.value;
    };

    const fromV = inputValueRequired && fromValue !== null ? resolveSelectApiValue(fromValue) : '';
    let toV = '';

    if (mainKey === 'annual_meeting_month') {
      if (selectedType.value === 'month_before' && fromV === '1') {
        snackbar.error('Invalid value for Before');
        return;
      }
      if (selectedType.value === 'month_after' && fromV === '12') {
        snackbar.error('Invalid value for month After');
        return;
      }
    }

    const key = getFilterKey(mainKey as string, subKey, subKey2, selectedType?.absoluteKey);
    if (showTwoValues && toValue !== null && inputValueRequired) {
      toV = resolveSelectApiValue(toValue);
    }

    const { filter: valueToApply, filterValue } = getFilterValue(type, key, showTwoValues ? [fromV, toV] : [fromV]);

    let fV = filterValue;
    if (isValueBasedFilter) {
      fV = options.find((f) => f.value === fromV)?.name || '';
      if (showTwoValues) {
        const y = options.find((f) => f.value === toV)?.name || '';
        fV += '|||' + y;
      }
    } else if (fromValue !== null) {
      fV =
        showTwoValues && toValue !== null
          ? `${getOptionLabel(fromValue)}|||${getOptionLabel(toValue)}`
          : getOptionLabel(fromValue);
    }

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        valueToApply,
        filterValue: fV,
        selectedType,
      });
    });
  }, [
    fromValue,
    getOptionLabel,
    handleSubmitAdvFilters,
    inputValueRequired,
    isValueBasedFilter,
    mainKey,
    options,
    perFilter,
    showTwoValues,
    startTransition,
    subKey,
    subKey2,
    toValue,
    type,
    types,
  ]);

  return (
    <section className={'flex flex-col'}>
      {inputValueRequired && (
        <div className="flex gap-1">
          <Select
            showSearchIcon
            menuListClassName="max-h-28! h-24"
            options={options}
            value={fromValue}
            onChange={setFromValue}
            getOptionLabel={getOptionLabel}
            getRef={(e) => setRefOne(e)}
            menuPortalTarget={null}
          />
          {showTwoValues && (
            <Select
              menuListClassName="max-h-28! h-24"
              options={options}
              getOptionLabel={getOptionLabel}
              value={toValue}
              onChange={setToValue}
              showSearchIcon
              getRef={(e) => setRefTwo(e)}
              menuPortalTarget={null}
            />
          )}
        </div>
      )}

      <FilterTypeSelector types={types} type={type} setType={setType} />

      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
